import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { BsCheckLg, BsImage, BsPlusLg, BsThreeDotsVertical, BsX } from "react-icons/bs";
import { NumericFormat } from "react-number-format";
import { createOfertaAoVivo, updateOfertaAoVivo } from "../../services/api";
import ProductImageGalleryModal from "../../components/ProductImageGalleryModal";
import { isHttpUrl } from "./offerPresentation";
import { createOfferDraft, draftCouponFields, draftHasChanges, IMAGE_TYPES, imageFileError, offerDraftPayload } from "./offerEditor";

const OfertaQuickForm = forwardRef(function OfertaQuickForm({ variant = "grid", visible = true, onSaved, onEditingChange }, ref) {
  const [form, setForm] = useState(createOfferDraft);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [showGallery, setShowGallery] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [showCouponMenu, setShowCouponMenu] = useState(false);
  const [session, setSession] = useState(0);
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  const titleRef = useRef(null);
  const readerRef = useRef(null);
  const generationRef = useRef(0);
  const busyRef = useRef(false);
  const galleryGenerationRef = useRef(0);
  const editing = !!form.original;

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const invalidateImageRead = () => {
    generationRef.current += 1;
    if (readerRef.current?.readyState === 1) readerRef.current.abort();
    readerRef.current = null;
    setReading(false);
  };

  useEffect(() => () => {
    generationRef.current += 1;
    if (readerRef.current?.readyState === 1) readerRef.current.abort();
  }, []);

  const reset = () => {
    invalidateImageRead();
    setForm(createOfferDraft());
    setShowGallery(false);
    setShowImageMenu(false);
    setShowCouponMenu(false);
    setError("");
    onEditingChange?.(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const focusEditor = useCallback(() => {
    titleRef.current?.focus({ preventScroll: true });
    rootRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, []);

  useImperativeHandle(ref, () => ({
    edit(offer) {
      if (busyRef.current) return false;
      if (form.original?.id === offer.id) { focusEditor(); return true; }
      if ((draftHasChanges(form) || reading) && !window.confirm("Descartar as alterações não salvas e editar outra oferta?")) return false;
      invalidateImageRead();
      setForm(createOfferDraft(offer));
      setShowGallery(false);
      setShowImageMenu(false);
      setShowCouponMenu(false);
      setError("");
      setWarning("");
      setSession(prev => prev + 1);
      onEditingChange?.(offer);
      return true;
    },
  }));

  useEffect(() => {
    if (session) focusEditor();
  }, [session, focusEditor]);

  const readImage = file => {
    if (busyRef.current) return false;
    const message = imageFileError(file);
    if (message) { setError(message); return true; }
    invalidateImageRead();
    const generation = generationRef.current;
    const reader = new FileReader();
    readerRef.current = reader;
    setReading(true);
    setError("");
    reader.onload = () => {
      if (generation !== generationRef.current) return;
      update("image", reader.result);
      setReading(false);
      readerRef.current = null;
    };
    reader.onerror = () => {
      if (generation !== generationRef.current) return;
      setError("Não foi possível ler a imagem. Tente novamente.");
      setReading(false);
      readerRef.current = null;
    };
    reader.readAsDataURL(file);
    return true;
  };

  const onPaste = event => {
    // React portals may bubble events from the gallery. Only handle this form's DOM.
    if (!visible || busyRef.current || !event.currentTarget.contains(event.target)) return;
    const clipboard = event.clipboardData;
    let file = null;
    for (const item of Array.from(clipboard?.items || [])) {
      if (item.kind === "file" && /^image\//i.test(item.type)) {
        file = item.getAsFile();
        if (file) break;
      }
    }
    if (!file) file = Array.from(clipboard?.files || []).find(item => /^image\//i.test(item.type));
    if (file && readImage(file)) { event.preventDefault(); return; }
    const text = clipboard?.getData("text/plain")?.trim() || "";
    if (event.target === fileRef.current) return;
    if (event.target.closest?.(".oav-quick-thumb") && isHttpUrl(text) && /\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(text)) {
      invalidateImageRead();
      update("image", text);
      setError("");
      event.preventDefault();
    }
  };

  const submit = async event => {
    event.preventDefault();
    if (busyRef.current || reading) return;
    if (!form.name.trim()) { setError("Preencha o título."); return; }
    const payload = offerDraftPayload(form);
    if (payload.offer_price != null && !Number.isFinite(payload.offer_price)) { setError("Preço inválido."); return; }
    busyRef.current = true;
    setBusy(true);
    setError("");
    setWarning("");
    try {
      const result = editing ? await updateOfertaAoVivo(form.original.id, payload) : await createOfertaAoVivo(payload);
      if (result?.success === false) throw new Error(result.error || "Não foi possível salvar a oferta.");
      reset();
      setWarning(result?.warning || "");
      onSaved?.(result);
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.error || data?.message || Object.values(data?.errors || {}).flat().join(" ") || err.message || "Falha ao salvar a oferta.");
      if (data?.warning) setWarning(data.warning);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const expandCoupon = () => {
    setForm(prev => ({ ...prev, ...draftCouponFields(prev), expanded: true }));
    setShowCouponMenu(false);
  };

  const updateCoupon = (key, value) => setForm(prev => {
    const next = { ...prev, [key]: value };
    return { ...next, cupomOrLink: next.cupom || next.link_resgate || "", primaryKind: next.cupom ? "cupom" : "link_resgate" };
  });

  return (
    <>
      <Form ref={rootRef} className={`oav-quick oav-quick--${variant} ${editing ? "is-editing" : ""}`}
        style={visible ? undefined : { display: "none" }} onSubmit={submit} onPaste={onPaste} aria-label={editing ? "Editar oferta inline" : "Cadastrar oferta inline"}>
        {editing && <div className="oav-quick-status" role="status">
          <strong>{form.original.code ? `Editando ${form.original.code}` : "Editando oferta"}</strong>
          <button type="button" disabled={busy} onClick={() => { reset(); setWarning(""); }}>Cancelar edição</button>
        </div>}
        <div className="oav-quick-fields">
          <Dropdown className="oav-quick-image" show={showImageMenu} onToggle={setShowImageMenu}>
            <Dropdown.Toggle as="button" type="button" className="oav-quick-thumb" disabled={busy}
              title="Adicionar imagem" aria-label="Imagem da oferta. Cole uma imagem com Ctrl+V ou abra as opções de imagem" aria-busy={reading}>
              {form.image ? <img src={form.image} alt="Imagem do rascunho da oferta" /> : <BsImage />}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as="button" type="button" onClick={() => fileRef.current?.click()}>Enviar imagem</Dropdown.Item>
              <Dropdown.Item as="button" type="button" onClick={() => {
                invalidateImageRead();
                galleryGenerationRef.current = generationRef.current;
                setShowGallery(true);
              }}>Selecionar da galeria</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(",")} hidden onChange={event => {
            const file = event.target.files?.[0];
            if (file) readImage(file);
            event.target.value = "";
          }} />
          <Form.Control ref={titleRef} className="oav-quick-title" disabled={busy} value={form.name}
            onChange={e => update("name", e.target.value)} placeholder="Título da oferta" aria-label="Título da oferta" />
          <NumericFormat className="oav-quick-price" disabled={busy} value={form.price} customInput={Form.Control}
            thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale={false} allowNegative={false} prefix="R$ "
            onValueChange={({ floatValue }) => update("price", floatValue ?? "")} placeholder="Preço" aria-label="Preço" />
          <Dropdown className="oav-quick-coupon-field" show={showCouponMenu} onToggle={setShowCouponMenu}>
            <Form.Control className="oav-quick-cupom" disabled={busy} readOnly={form.expanded} value={form.cupomOrLink}
              onChange={e => update("cupomOrLink", e.target.value)} placeholder="Cupom ou link" aria-label="Cupom ou link" />
            <Dropdown.Toggle as="button" type="button" className="oav-quick-coupon-options" disabled={busy}
              aria-label="Opções de cupom e resgate" title="Opções de cupom e resgate">
              <BsThreeDotsVertical aria-hidden="true" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as="button" type="button" onClick={expandCoupon}>Editar código e resgate</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Form.Control className="oav-quick-link" disabled={busy} value={form.link}
            onChange={e => update("link", e.target.value)} placeholder="Link da oferta (opcional)" aria-label="Link da oferta" />
          <button type="submit" className="oav-quick-add" disabled={busy || reading}
            title={editing ? "Salvar alterações" : "Adicionar oferta"} aria-label={editing ? "Salvar alterações" : "Adicionar oferta"}>
            {busy ? "…" : editing ? <BsCheckLg /> : <BsPlusLg />}
          </button>
        </div>
        {form.expanded && <div className="oav-quick-expanded">
          <button type="button" className="oav-quick-expanded-close" disabled={busy}
            aria-label="Fechar opções de cupom e resgate" onClick={() => setForm(prev => ({ ...prev, expanded: false }))}><BsX /></button>
          <Form.Label htmlFor="oav-inline-cupom">Cupom</Form.Label>
          <Form.Control id="oav-inline-cupom" disabled={busy} value={form.cupom} onChange={e => updateCoupon("cupom", e.target.value)} aria-label="Código do cupom" />
          <Form.Label htmlFor="oav-inline-resgate">Link de resgate do cupom</Form.Label>
          <Form.Control id="oav-inline-resgate" disabled={busy} value={form.link_resgate} onChange={e => updateCoupon("link_resgate", e.target.value)} placeholder="https://..." aria-label="Link de resgate do cupom" />
        </div>}
        {warning && <span className="oav-quick-warning" role="status">{warning}</span>}
        {error && <span className="oav-quick-error" role="alert">{error}</span>}
      </Form>
      {!visible && warning && <div className="oav-quick-warning oav-results-message" role="status">{warning}</div>}
      <ProductImageGalleryModal show={showGallery} onHide={() => setShowGallery(false)} onSelect={image => {
        if (galleryGenerationRef.current !== generationRef.current || busyRef.current) return;
        invalidateImageRead();
        update("image", image.url || image.thumbnail_url || "");
        setShowGallery(false);
        setError("");
      }} />
    </>
  );
});

export default OfertaQuickForm;
